import { createServerFn } from "@tanstack/react-start";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Json } from "./oddpool.server";

const today = () => new Date().toISOString().split("T")[0] as string;

function parseJson(raw: string): Record<string, Json> {
  const cleaned = raw.replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();
  try {
    return JSON.parse(cleaned) as Record<string, Json>;
  } catch {
    return {};
  }
}

/** Admin: AI read on a single prediction market event. */
export const aiMarketAnalyst = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { event_id: string; title?: string; persist?: boolean }) => input)
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("./authz.server");
    const { oddpoolFetch } = await import("./oddpool.server");
    const { geminiChat, NO_EM_DASH_RULE } = await import("./ai.server");

    await assertAdmin(context.supabase, context.userId);
    if (!data.event_id) throw new Error("event_id required");

    const id = encodeURIComponent(data.event_id);
    const [marketsRes, ohlcvRes, whalesRes] = await Promise.all([
      oddpoolFetch(`/search/events/${id}/markets`),
      oddpoolFetch(`/events/${id}/ohlcv?interval=1d`),
      oddpoolFetch(`/whales/user/event/${id}?limit=20`).catch(() => null),
    ]);
    const markets = await marketsRes.json().catch(() => ({}));
    const ohlcv = await ohlcvRes.json().catch(() => ({}));
    const whales = whalesRes && whalesRes.ok ? await whalesRes.json().catch(() => ({})) : null;

    const system = `You are a prediction-market analyst. Today is ${today()}. Reply ONLY with JSON matching the schema:
{
 "recommendation": "yes" | "no" | "neutral",
 "confidence": 0-100,
 "summary": "2-3 sentences",
 "bull_case": ["..."],
 "bear_case": ["..."],
 "key_risks": ["..."],
 "suggested_outcome_market_id": "string or null"
}${NO_EM_DASH_RULE}`;

    const user = `Event: ${data.title || data.event_id}
Markets: ${JSON.stringify(markets).slice(0, 4000)}
OHLCV (recent): ${JSON.stringify(ohlcv).slice(0, 3000)}
Whale trades: ${JSON.stringify(whales).slice(0, 3000)}`;

    const content = await geminiChat(
      [
        { role: "system", content: system },
        { role: "user", content: user },
      ],
      { temperature: 0.4, responseFormat: { type: "json_object" } },
    );
    const analysis = parseJson(content);

    if (data.persist !== false) {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      await supabaseAdmin.from("event_ai_analyses").insert({
        event_id: data.event_id,
        analysis,
        model: "gemini-3.7-flash",
        is_latest: true,
      } as never);
    }

    return { analysis };
  });

/** Admin: plain-language read on a cross-venue arbitrage opportunity. */
export const aiArbitrageExplainer = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { opportunity: Record<string, unknown> }) => input)
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("./authz.server");
    const { geminiChat, NO_EM_DASH_RULE } = await import("./ai.server");

    await assertAdmin(context.supabase, context.userId);
    if (!data.opportunity) throw new Error("opportunity required");

    const content = await geminiChat(
      [
        {
          role: "system",
          content: `You analyze cross-venue prediction-market arbitrage. Reply JSON: {"why":"1-2 sentences","execution_risk":"low|medium|high","risk_reason":"...","suggested_size_usd":number,"action_steps":["..."]}.${NO_EM_DASH_RULE}`,
        },
        { role: "user", content: `Arb opp: ${JSON.stringify(data.opportunity)}` },
      ],
      { temperature: 0.3, responseFormat: { type: "json_object" } },
    );

    return { explanation: parseJson(content) };
  });

interface WhaleInsight {
  trade_id: string;
  classification: string;
  confidence: number;
  narrative: string;
}

/** Admin: classify recent whale trades. */
export const aiWhaleInterpreter = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { event_id?: string; limit?: number }) => input ?? {})
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("./authz.server");
    const { oddpoolFetch } = await import("./oddpool.server");
    const { geminiChat, NO_EM_DASH_RULE } = await import("./ai.server");

    await assertAdmin(context.supabase, context.userId);
    const limit = data.limit ?? 25;

    const path = data.event_id
      ? `/whales/user/event/${encodeURIComponent(data.event_id)}?limit=${limit}`
      : `/whales/user/feed?limit=${limit}`;
    const wRes = await oddpoolFetch(path);
    const whales = (await wRes.json().catch(() => ({}))) as Record<string, unknown>;
    const trades = ((whales["trades"] ?? whales["data"] ?? []) as Array<Record<string, unknown>>) || [];
    if (trades.length === 0) return { insights: [] as WhaleInsight[] };

    const content = await geminiChat(
      [
        {
          role: "system",
          content: `Classify each whale trade as one of: accumulation, distribution, hedge, speculation, exit. Reply JSON {"insights":[{"trade_id":"","classification":"","confidence":0-100,"narrative":""}]}.${NO_EM_DASH_RULE}`,
        },
        { role: "user", content: JSON.stringify(trades).slice(0, 6000) },
      ],
      { temperature: 0.3, responseFormat: { type: "json_object" } },
    );

    const parsedInsights = parseJson(content)["insights"];
    const insights: WhaleInsight[] = Array.isArray(parsedInsights)
      ? (parsedInsights as unknown as WhaleInsight[])
      : [];

    if (insights.length > 0) {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const rows = insights.map((i) => {
        const orig =
          trades.find((t) => t["trade_id"] === i.trade_id || t["id"] === i.trade_id) ?? {};
        return {
          trade_id: i.trade_id,
          event_id: (orig["event_id"] as string) ?? data.event_id ?? null,
          market_id: (orig["market_id"] as string) ?? null,
          venue: (orig["venue"] as string) ?? null,
          classification: i.classification,
          confidence: i.confidence,
          narrative: i.narrative,
          payload: orig,
        };
      });
      await supabaseAdmin.from("ai_whale_insights").insert(rows as never);
    }

    return { insights };
  });

/** Admin: build and store the daily prediction market digest. */
export const aiDailyDigest = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { assertAdmin } = await import("./authz.server");
    const { oddpoolFetch } = await import("./oddpool.server");
    const { geminiChat, NO_EM_DASH_RULE } = await import("./ai.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    await assertAdmin(context.supabase, context.userId);

    const [arbRes, whaleRes, recentRes] = await Promise.all([
      oddpoolFetch("/arbitrage/current?min_net_cents=0.5&limit=10"),
      oddpoolFetch("/whales/user/feed?limit=20").catch(() => null),
      oddpoolFetch("/search/recent/events?limit=20"),
    ]);
    const arb = await arbRes.json().catch(() => ({}));
    const whales = whaleRes && whaleRes.ok ? await whaleRes.json().catch(() => ({})) : {};
    const recent = await recentRes.json().catch(() => ({}));

    const digestDate = today();
    const content = await geminiChat(
      [
        {
          role: "system",
          content: `You write a daily prediction-market digest for ${digestDate}. Reply JSON:
{"headline":"","summary":"2-3 sentences","top_arbs":[{"title":"","net_cents":0,"why":""}],"whale_moves":[{"title":"","narrative":""}],"shifts":[{"title":"","change":""}]}${NO_EM_DASH_RULE}`,
        },
        {
          role: "user",
          content: `ARB:${JSON.stringify(arb).slice(0, 3500)}\nWHALES:${JSON.stringify(whales).slice(0, 2500)}\nRECENT:${JSON.stringify(recent).slice(0, 2500)}`,
        },
      ],
      { temperature: 0.5, responseFormat: { type: "json_object" } },
    );
    const digest = parseJson(content);

    await supabaseAdmin.from("ai_daily_digests").upsert(
      {
        digest_date: digestDate,
        payload: digest,
        published_at: new Date().toISOString(),
      } as never,
      { onConflict: "digest_date" },
    );

    return { digest, digest_date: digestDate };
  });

const COPILOT_TOOLS = [
  {
    type: "function",
    function: {
      name: "searchMarkets",
      description: "Full-text search prediction markets across Kalshi and Polymarket",
      parameters: {
        type: "object",
        properties: { query: { type: "string" }, limit: { type: "number" } },
        required: ["query"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "getEventMarkets",
      description: "List markets under an event",
      parameters: {
        type: "object",
        properties: { event_id: { type: "string" } },
        required: ["event_id"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "getArbitrage",
      description: "List current risk-free cross-venue arbitrage opportunities",
      parameters: { type: "object", properties: { min_net_cents: { type: "number" } } },
    },
  },
  {
    type: "function",
    function: {
      name: "getWhaleFeed",
      description: "Recent whale trades for tracked events",
      parameters: { type: "object", properties: { limit: { type: "number" } } },
    },
  },
];

async function runCopilotTool(name: string, args: Record<string, unknown>): Promise<unknown> {
  const { oddpoolFetch } = await import("./oddpool.server");
  let path = "";
  if (name === "searchMarkets")
    path = `/search/events?q=${encodeURIComponent(String(args["query"]))}&limit=${args["limit"] ?? 20}`;
  else if (name === "getEventMarkets")
    path = `/search/events/${encodeURIComponent(String(args["event_id"]))}/markets`;
  else if (name === "getArbitrage")
    path = `/arbitrage/current?min_net_cents=${args["min_net_cents"] ?? 0.5}&limit=20`;
  else if (name === "getWhaleFeed") path = `/whales/user/feed?limit=${args["limit"] ?? 25}`;
  else return { error: "unknown_tool" };

  const r = await oddpoolFetch(path);
  return await r.json().catch(() => ({ error: "parse" }));
}

/** Tool-using research chat over live prediction market data. */
export const aiResearchCopilot = createServerFn({ method: "POST" })
  .inputValidator((input: { messages: Array<{ role: string; content: string }> }) => input)
  .handler(async ({ data }): Promise<{ reply: string }> => {
    const { NO_EM_DASH_RULE, GEMINI_MODEL } = await import("./ai.server");

    const messages = data.messages;
    if (!Array.isArray(messages) || messages.length === 0) {
      throw new Error("messages is required and must contain at least one user message");
    }

    const key = process.env["GEMINI_API_KEY"];
    if (!key) throw new Error("GEMINI_API_KEY is not configured");

    const convo: Array<Record<string, unknown>> = [
      {
        role: "system",
        content: `You are a prediction-market research copilot. Today is ${today()}. Use tools to fetch live Kalshi and Polymarket data. Cite event titles. Be concise; use markdown.${NO_EM_DASH_RULE}`,
      },
      ...messages,
    ];

    for (let i = 0; i < 4; i++) {
      const res = await fetch(
        "https://generativelanguage.googleapis.com/v1beta/openai/chat/completions",
        {
          method: "POST",
          headers: { Authorization: `Bearer ${key}`, "Content-Type": "application/json" },
          body: JSON.stringify({
            model: GEMINI_MODEL,
            temperature: 0.4,
            messages: convo,
            tools: COPILOT_TOOLS,
          }),
        },
      );
      const j = await res.json();
      if (!res.ok) throw new Error(j?.error?.message || `Gemini error ${res.status}`);

      const msg = j?.choices?.[0]?.message;
      convo.push(msg);
      const calls = msg?.tool_calls ?? [];
      if (calls.length === 0) return { reply: String(msg?.content ?? "") };

      for (const c of calls) {
        const args = parseJson(c.function?.arguments || "{}");
        const result = await runCopilotTool(c.function?.name, args);
        convo.push({
          role: "tool",
          tool_call_id: c.id,
          content: JSON.stringify(result).slice(0, 8000),
        });
      }
    }

    return { reply: "Reached tool-call limit." };
  });
